import {
  Zap, CalendarDays, CheckCircle, Target, StickyNote, Moon,
  Dumbbell, Apple, Wind, Heart, GraduationCap, Swords, Bell,
  Baby, Brain, MessageSquare, Sparkles, Briefcase,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BABY_MODULE_ENABLED } from "@/lib/featureFlags";

// ── Types ────────────────────────────────────────────────────────────────────

export type ModuleCategory = "core" | "special" | "hidden";

export interface ModuleDefinition {
  id: string;
  icon: LucideIcon;
  route: string;
  category: ModuleCategory;
  defaultVisible: boolean;
  premiumOnly: boolean;
  /** Profile field that must be truthy for this module to be visible */
  profileCondition?: "hasChild";
  /** i18n key prefix — name = `module.{i18nKey}.name`, sub = `module.{i18nKey}.sub` */
  i18nKey: string;
  /** Optional feature flag — `false` hides the module from the /modules grid */
  enabled?: boolean;
}

// ── Official module registry ─────────────────────────────────────────────────

export const MODULE_REGISTRY: ModuleDefinition[] = [
  // Unified module grid. Fitness, Nutrition and Brain follow Languages.
  { id: "checkin",    icon: Zap,           route: "/check-in",         category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "checkin"    },
  { id: "calendar",   icon: CalendarDays,  route: "/agenda",           category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "calendar"   },
  { id: "language",   icon: MessageSquare, route: "/language",         category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "language"   },
  { id: "fitness",    icon: Dumbbell,      route: "/fitness",          category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "fitness"    },
  { id: "nutrition",  icon: Apple,         route: "/nutrition",        category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "nutrition"  },
  { id: "brain",      icon: Brain,         route: "/brain-training",   category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "brain"      },
  { id: "habits",     icon: CheckCircle,   route: "/habits",           category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "habits"     },
  { id: "goals",      icon: Target,        route: "/goals",            category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "goals"      },
  { id: "focus",      icon: Zap,           route: "/focus",            category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "focus"      },
  { id: "sleep",      icon: Moon,          route: "/sleep",            category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "sleep"      },
  // mywork: módulo genérico de trabalho/moradia (white-label B2B, decisões
  // ee013ee6/f89c881e). Visível SÓ para a allow-list de e-mail — o gate vive
  // em useModules via useMyWorkAccess (aplicado nos TRÊS memos, ver a
  // armadilha do getModulesByCategory lá); o registry continua declarativo.
  { id: "mywork",     icon: Briefcase,     route: "/my-work",          category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "mywork"     },

  // Legacy Baby entry remains disabled by its existing feature flag.
  { id: "baby",       icon: Baby,          route: "/baby",             category: "special", defaultVisible: true,  premiumOnly: false, i18nKey: "baby",       enabled: BABY_MODULE_ENABLED },
  // Notes: hidden from Modules grid; accessed via Agenda header button (CHUNK 15-postbrain).
  // Route /notes preserved.
  { id: "notes",      icon: StickyNote,    route: "/notes",            category: "hidden",  defaultVisible: true,  premiumOnly: false, i18nKey: "notes"      },

  // ─── HIDDEN (exist in code, not shown in modules page) ──────────────────
  // finance: unhidden for the Finance Phase 1 launch (manual CRUD + accounts +
  // budgets + history). Free for everyone; voice/extraction ships in a later phase.
  { id: "finance",    icon: Sparkles,      route: "/finance",          category: "core",    defaultVisible: true,  premiumOnly: false, i18nKey: "finance"    },
  { id: "meditation", icon: Wind,          route: "/meditation",       category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "meditation" },
  { id: "social",     icon: Heart,         route: "/relationships",    category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "social"     },
  { id: "learning",   icon: GraduationCap, route: "/learning",         category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "learning"   },
  { id: "challenge",  icon: Swords,        route: "/mirror-challenge", category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "challenge"  },
  { id: "alert",      icon: Bell,          route: "/mirror-whisper",   category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "alert"      },
  { id: "gratitude",  icon: Heart,         route: "/gratitude",        category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "gratitude"  },
  { id: "travel",     icon: Sparkles,      route: "/travel",           category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "travel"     },
  { id: "vision",     icon: Sparkles,      route: "/vision-board",     category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "visionBoard"},
  { id: "tasks",      icon: Sparkles,      route: "/tasks",            category: "hidden",  defaultVisible: false, premiumOnly: false, i18nKey: "tasks"      },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** IDs of modules that are valid for display (core + special) */
export const VALID_MODULE_IDS = new Set(
  MODULE_REGISTRY.filter((m) => m.category !== "hidden").map((m) => m.id),
);

/** Get only visible modules (excludes hidden category) */
export function getVisibleModules(): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => m.category !== "hidden");
}

/** Get modules by category */
export function getModulesByCategory(category: ModuleCategory): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => m.category === category);
}

/** Find a module by id */
export function getModuleById(id: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => m.id === id);
}
